import * as THREE from 'three';

/**
 * Resolves the world buffer into the composer: applies ambient occlusion and
 * per-pixel camera motion blur in a single fullscreen pass.
 *
 * AO arrives as a separate map rather than pre-multiplied, which lets us skip
 * GTAOPass's own copy-then-multiply output stage — that stage costs a full HDR
 * target and two extra fullscreen draws to do what one multiply does here.
 *
 * Motion blur reconstructs each pixel's world position from depth with the
 * current inverse view-projection, re-projects it through *last* frame's
 * view-projection, and smears along the resulting screen-space velocity. That
 * captures rotation, translation and the ADS FOV change for free. Object motion
 * is not captured — that needs a velocity buffer, and a second scene pass is a
 * bad trade for an effect that should be barely visible.
 */
export const WorldResolveShader = {
  name: 'WorldResolveShader',

  defines: {
    MB_SAMPLES: 6,
  },

  uniforms: {
    tDiffuse: { value: null },
    tDepth: { value: null },
    tAO: { value: null },
    uAOIntensity: { value: 0.0 },
    uInvViewProj: { value: new THREE.Matrix4() },
    uPrevViewProj: { value: new THREE.Matrix4() },
    uStrength: { value: 0.0 },
    uMaxVelocity: { value: new THREE.Vector2(0.006, 0.006) },
  },

  vertexShader: /* glsl */ `
    varying vec2 vUv;
    void main() {
      vUv = uv;
      gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
    }
  `,

  fragmentShader: /* glsl */ `
    varying vec2 vUv;

    uniform sampler2D tDiffuse;
    uniform highp sampler2D tDepth;
    uniform sampler2D tAO;
    uniform float uAOIntensity;
    uniform mat4 uInvViewProj;
    uniform mat4 uPrevViewProj;
    uniform float uStrength;
    uniform vec2 uMaxVelocity;

    vec3 occlude(vec3 c, vec2 uv) {
      if (uAOIntensity <= 0.0) return c;
      vec3 ao = texture2D(tAO, uv).rgb;
      return c * mix(vec3(1.0), ao, uAOIntensity);
    }

    void main() {
      vec3 base = occlude(texture2D(tDiffuse, vUv).rgb, vUv);

      if (uStrength <= 0.0) {
        gl_FragColor = vec4(base, 1.0);
        return;
      }

      float d = texture2D(tDepth, vUv).x;

      vec4 clip = vec4(vUv * 2.0 - 1.0, d * 2.0 - 1.0, 1.0);
      vec4 world = uInvViewProj * clip;
      world /= world.w;
      vec4 prevClip = uPrevViewProj * world;

      // Points that fell behind the previous frame's near plane re-project to
      // garbage; leave them sharp rather than smearing them across the frame.
      if (prevClip.w <= 0.0) {
        gl_FragColor = vec4(base, 1.0);
        return;
      }

      vec2 prevUv = (prevClip.xy / prevClip.w) * 0.5 + 0.5;
      vec2 vel = clamp((vUv - prevUv) * uStrength, -uMaxVelocity, uMaxVelocity);

      // Under about a tenth of a pixel there is nothing to smear.
      if (dot(vel, vel) < 1e-10) {
        gl_FragColor = vec4(base, 1.0);
        return;
      }

      vec3 sum = base;
      float w = 1.0;
      for (int i = 1; i <= MB_SAMPLES; i++) {
        vec2 uv = clamp(vUv - vel * (float(i) / float(MB_SAMPLES)), vec2(0.0), vec2(1.0));
        sum += occlude(texture2D(tDiffuse, uv).rgb, uv);
        w += 1.0;
      }

      gl_FragColor = vec4(sum / w, 1.0);
    }
  `,
};
